var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, html, LitElement, render } from "lit";
import { customElement, property } from "lit/decorators.js";
import "./StackComponent";
import "./HeapComponent";
import { defaults, outlineColor, outlineVariantColor } from "../style";
import { ref } from "lit/directives/ref.js";
import { BezierConnector, newInstance } from "@jsplumb/browser-ui";
import { deepAwaitMixin } from "../mixins/DeepAwaitMixin";
let FrameComponent = class FrameComponent extends deepAwaitMixin(LitElement) {
    update(changedProperties) {
        super.update(changedProperties);
        if (changedProperties.has("frame")) {
            this.heap.clearMargins();
        }
    }
    get jsplumb() {
        if (this.jsplumbInstance === undefined && this.canvas !== undefined) {
            this.jsplumbInstance = newInstance({
                container: this.canvas,
            });
        }
        return this.jsplumbInstance;
    }
    async addConnection(from, to, highlight = true) {
        const removeConnection = (e) => {
            this.removeConnection(e.detail.from, e.detail.to);
            e.detail.from.removeEventListener("remove-reference", removeConnection);
        };
        from.addEventListener("remove-reference", removeConnection);
        await this.updateComplete;
        const target = this.heap.endpointMap[to];
        if (target === undefined) {
            // the target is not rendered on the heap, so it should be rendered inlined in the reference
            render(html `<tc-heap-element .el=${this.layout.heap[to]}></tc-heap-element>`, from);
            return;
        }
        else {
            this.heap.addReference(to);
        }
        this.jsplumb.connect({
            source: from,
            target: this.heap.endpointMap[to],
            anchors: ["Center", "Left"],
            endpoints: [{ type: "Dot", options: { radius: 3, cssClass: highlight ? "highlight" : "" } }, "Blank"],
            overlays: [{ type: "Arrow", options: { location: 1, width: 10, length: 10 } }],
            connector: { type: BezierConnector.type, options: { curviness: 50 } },
            cssClass: highlight ? "highlight" : "",
        }); // eslint-disable-line @typescript-eslint/no-explicit-any
    }
    removeConnection(from, to) {
        this.jsplumb.select({ source: from }).deleteAll();
        this.heap.removeReference(to);
    }
    updated(_changedProperties) {
        super.updated(_changedProperties);
        this.scheduleRedraw();
    }
    async scheduleRedraw() {
        await this.deepUpdateComplete;
        this.jsplumb.setSuspendDrawing(false, true);
        this.jsplumb.repaintEverything();
    }
    get updateComplete() {
        return Promise.all([this.heap.updateComplete, this.stack.updateComplete])
            .then(() => super.updateComplete);
    }
    render() {
        this.jsplumb?.setSuspendDrawing(true);
        return html `
            ${this.frame.event == "exception" ? html `
                <tc-exception-card>
                    ${this.frame.exception_msg}
                </tc-exception-card>
            ` : html ``}
            <div class="canvas" ${ref(e => this.canvas = e)}>
                <tc-stack 
                    .frame=${this.frame}
                    ${ref(e => this.stack = e)}
                    @add-reference="${(e) => this.addConnection(e.detail.from, e.detail.to, e.detail.highlight)}"
                ></tc-stack>
                <tc-heap
                    .layout=${this.layout}
                    ${ref(e => this.heap = e)}
                    @add-reference="${(e) => this.addConnection(e.detail.from, e.detail.to)}"
                    @deep-update="${() => this.scheduleRedraw()}"
                ></tc-heap>
            </div>
        `;
    }
};
FrameComponent.styles = [defaults, css `
        .canvas {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: row;
        }
        tc-stack {
          margin-right: 64px;
        }
        
        svg path {
          stroke: ${outlineVariantColor};
          stroke-width: 1px;
        }
      
        .jtk-overlay {
          fill: ${outlineVariantColor};
          stroke-width: 1px;
        }
      
        svg circle {
          fill: ${outlineVariantColor};
          stroke-width: 59;
          stroke-linecap: round;
        }
      
        svg.highlight path {
            stroke: ${outlineColor};
        }
      
        svg.highlight circle {
            fill: ${outlineColor};
        }
      
        .highlight .jtk-overlay {
            fill: ${outlineColor};
        }    
        
        tc-exception-card {
          margin-bottom: 20px;
        }
    `];
__decorate([
    property({ type: Object })
], FrameComponent.prototype, "frame", void 0);
__decorate([
    property({ type: Object })
], FrameComponent.prototype, "layout", void 0);
FrameComponent = __decorate([
    customElement('tc-frame')
], FrameComponent);
export { FrameComponent };
//# sourceMappingURL=FrameComponent.js.map